// HI1 — **ONE HEADER ICON BUTTON, RENDERED TWICE.** Her order: both icons bigger, the message one
// carrying a light aqua glow, badges still on both.
//
// It is its own file for the reason every leaf on this line is (R8): it takes props, holds no state
// and calls no hook, so `scripts/hi1-header-icons.mjs` CALLS it and reads the tree back — the badge
// at zero, at three and at twelve, the glow on the mail and NOT on the bell, and the accessible name
// in both languages. Inside `HouseInbox` none of that was reachable: that component holds four hooks
// and polls, so a live browser with real unread rows was the only instrument that could see any of
// it, and the badge rule was typed twice three lines apart with nothing between the copies.
import { Bell, Mail } from "lucide-react";
import { HEADER_ICON, HEADER_ICON_KINDS, headerBadgeText, headerIconGlows, headerIconLabelKey } from "../headerIcons.js";
import { t } from "../strings.js";
import { DEFAULT_LANGUAGE } from "../language.js";

// R25's DRAWER_ICONS shape, one size up: the face is resolved from a ROSTER held against
// `HEADER_ICON_KINDS` both directions, so a kind nobody rostered draws NO GLYPH rather than a
// plausible wrong one. A bell where a mail belongs is a lie about which door she is tapping, and it
// has no error state — she simply opens the wrong thing.
export const HEADER_ICON_FACES: Record<string, typeof Bell> = {
  notifications: Bell,
  messages: Mail,
};

export function HeaderIconButton({
  kind, unread, onClick, language = DEFAULT_LANGUAGE,
}: { kind: string; unread: number; onClick: () => void; language?: string }) {
  const badge = headerBadgeText(unread);
  const glows = headerIconGlows(kind);
  const Face = HEADER_ICON_KINDS.includes(kind) ? HEADER_ICON_FACES[kind] : null;
  const labelKey = headerIconLabelKey(kind, unread);
  return (
    <button
      type="button"
      className={glows ? "house-inbox__btn house-inbox__btn--glow" : "house-inbox__btn"}
      data-header-icon={kind}
      aria-label={labelKey ? t(language, labelKey, { count: unread }) : ""}
      onClick={onClick}
    >
      {/* The glyph carries the ONE declared size, never a literal, so the stylesheet's tap target and
          the ink inside it cannot drift apart (HI1's whole reason for being a shared law). */}
      {Face ? <Face size={HEADER_ICON.glyph} /> : null}
      {badge ? <em className="house-inbox__dot">{badge}</em> : null}
    </button>
  );
}
